import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { updateUnits } from "@Services/utils/helpers";
import _ from "lodash";
import type { ViewerSetting } from "@ServerTypes/accounts/accounts.schema";
import type { ReferenceMeasurement } from "@ServerTypes/measurements/measurements.schema";
import type { ViewRecord } from "@Types/views";

interface ModalEntry {
  show?: boolean;
  flagForAnimation?: boolean;
}

type HoopsModalState = Record<string, ModalEntry>;

const hoopsModalSlice = createSlice({
  name: "HoopsModalStatus",
  initialState: {} as HoopsModalState,
  reducers: {
    setShowOnlyModal: (state: HoopsModalState, action: PayloadAction<string | string[]>) => {
      const keysToShow = Array.isArray(action.payload) ? action.payload : [action.payload];

      Object.keys(state).forEach((key: string) => {
        if (!keysToShow.includes(key) && key !== "Viewer" && state[key]) {
          state[key]!.show = false;
        }
      });

      keysToShow.forEach((key: string) => {
        if (!Object.hasOwn(state, key)) {
          state[key] = {};
        }
        state[key]!.show = true;
      });
    },
    setModalStatus: (_state: HoopsModalState, action: PayloadAction<HoopsModalState>) => action.payload,
    setAnimationForIssueCreation: (state: HoopsModalState, action: PayloadAction<boolean>) => ({
      ...state,
      Issues: {
        ...state.Issues,
        flagForAnimation: action.payload,
      },
    }),
  },
});

const sheetActiveSlice = createSlice({
  name: "isSheetActive",
  initialState: false,
  reducers: {
    setIsSheetActive: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const faceSelectionSlice = createSlice({
  name: "faceSelection",
  initialState: false,
  reducers: {
    setFaceSelection: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

type ModelPropertiesState = {
  data: Record<string, Record<number, any>> | any[];
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  // Additional dynamic fields (e.g., isFileFormTemplatesLoading, formTemplates, etc.)
  isFileFormTemplatesLoading?: boolean;
  isFileFormTemplatesLoaded?: boolean;
  defaultTemplate?: Record<string, any> | null;
  formTemplates?: Record<string, any> | null;
  questionIdAndNameMapper?: Record<string, string>;
};

const defaultModelProperties: ModelPropertiesState = {
  data: {}, // ✅ object, not []
  isLoading: true,
  isLoaded: false,
  isError: false,
};

interface ModifyMetadataUnitsPayload {
  fileVersionId: string;
  unitsNew: Record<string, any>;
  portalUnits: Record<string, any> | null | undefined;
}

interface UpdateCustomMetadataPayload {
  fileVersionId: string;
  nodeId: number[];
  fieldsData: { _id: string; value: string | number | boolean | string[] | null };
  selectedFormTemplate: string;
  engineType?: string;
}

interface UpdateQuantityAndUnitPayload {
  fileVersionId: string;
  nodeId: number;
  unit?: string | number | null;
  quantity?: string | number | null;
}

interface AssetDataItem {
  fileVersionId: string;
  nodeId: number;
}

interface UpdateAssetDataPayload {
  property: string;
  data: AssetDataItem[];
  action: "add" | "remove" | string;
}

const modelPropertiesSlice = createSlice({
  name: "modelProperties",
  initialState: defaultModelProperties,
  reducers: {
    setModelProperties: (state: ModelPropertiesState, action: PayloadAction<Partial<ModelPropertiesState>>) => ({
      ...state,
      ...action.payload,
    }),
    modifyMetadataUnits: (state: ModelPropertiesState, action: PayloadAction<ModifyMetadataUnitsPayload>) => {
      const { fileVersionId, unitsNew, portalUnits } = action.payload;
      const filesProperties = state.data?.[fileVersionId];
      if (!filesProperties) return state;
      const updatedDataByNodeId = Object.entries(filesProperties).reduce<Record<string, any>>(
        (acc, [nodeId, node]: [string, any]) => {
          const updatedProperties = updateUnits(node.properties, unitsNew, portalUnits);
          const updatedTypeProperties = updateUnits(node.typeProperties, unitsNew, portalUnits);
          acc[nodeId] = {
            ...node,
            properties: updatedProperties,
            typeProperties: updatedTypeProperties,
          };
          return acc;
        },
        {},
      );
      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: updatedDataByNodeId,
        },
      };
    },
    updateCustomMetadataInModelProperties: (
      state: ModelPropertiesState,
      action: PayloadAction<UpdateCustomMetadataPayload>,
    ) => {
      const { fileVersionId, nodeId, fieldsData, selectedFormTemplate } = action.payload;

      if (!state.data[fileVersionId]) {
        state.data[fileVersionId] = {};
      }
      const nodesMap = state.data[fileVersionId];
      const isEmptyArray = Array.isArray(fieldsData.value) && fieldsData.value.length === 0;

      nodeId.forEach((node: number) => {
        const existingItem = nodesMap[node];
        const updatedCustomProperties = { ...(existingItem?.customProperties || {}) };

        if (isEmptyArray) {
          delete updatedCustomProperties[fieldsData._id];
        } else {
          updatedCustomProperties[fieldsData._id] = fieldsData.value;
        }

        nodesMap[node] = {
          ...existingItem,
          fileVersionId,
          nodeId: node,
          selectedFormTemplate: { _id: selectedFormTemplate },
          customProperties: updatedCustomProperties,
        };
      });
    },
    updateQuantityAndUnit: (state: ModelPropertiesState, action: PayloadAction<UpdateQuantityAndUnitPayload>) => {
      const { fileVersionId, nodeId, unit, quantity } = action.payload;

      const node = _.get(state, ["data", fileVersionId, nodeId]);
      if (!node) return state;

      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: {
            ...state.data[fileVersionId],
            [nodeId]: {
              ...node,
              properties: {
                ...node.properties,
                Unit: unit ?? node.properties.Unit,
                Quantity: quantity ?? node.properties.Quantity,
              },
            },
          },
        },
      };
    },
    updateAssetDataInModelProperties: (state: ModelPropertiesState, reduxAction: PayloadAction<UpdateAssetDataPayload>) => {
      const { property, data, action } = reduxAction.payload;
      for (const obj of data) {
        const nodeData = _.get(state, ["data", obj.fileVersionId, obj.nodeId]);
        if (nodeData) {
          if (!Array.isArray(nodeData.assetData)) {
            nodeData.assetData = [];
          }
          if (action === "add") {
            if (!nodeData.assetData.includes(property)) {
              nodeData.assetData.push(property);
            }
          } else if (action === "remove") {
            nodeData.assetData = nodeData.assetData.filter((item: string) => item != property);
          }
        }
      }
    },
  },
});

type ModifiedModelPropsState = {
  nodeId: number[] | number;
  fileVersionId: string;
  fieldsData: any;
} | null;

const modifiedModelPropSlice = createSlice({
  name: "modifiedModelProps",
  initialState: null as ModifiedModelPropsState,
  reducers: {
    setModifiedModelProps: (state, action) => {
      return action.payload;
    },
  },
});

const settingModalSlice = createSlice({
  name: "GetSettingModalData",
  initialState: {} as ViewerSetting,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSettingModalAPIData: (_state: ViewerSetting, action: PayloadAction<any>) =>
      (action.payload as ViewerSetting) ?? {},
    updateSettingModalAPIData: (
      state: ViewerSetting,
      action: PayloadAction<Partial<ViewerSetting>>,
    ) => ({ ...state, ...action.payload }),
  },
});

interface NodeIdUniqueIdFileEntry {
  /** Maps nodeId (string) → cubeUniqueId (string). Values are strings at runtime. */
  nodeIdMap: Record<string, string | number | boolean | null> | null;
  cubeUniqueIdMap: Record<string, number[]>;
}

type NodeIdAndUniqueIdMapState = Record<string, NodeIdUniqueIdFileEntry>;

interface NodeIdAndUniqueIdMapPayload {
  fileVersionId: string;
  /** Map of nodeId → cubeUniqueId values. Values are strings at runtime. */
  data: Record<string, string | number | boolean | null> | null;
}

const nodeIdAndUniqueIdMapSlice = createSlice({
  name: "nodeIdAndUniqueIdMap",
  initialState: {} as NodeIdAndUniqueIdMapState,
  reducers: {
    setNodeIdAndUniqueIdMap: (state: NodeIdAndUniqueIdMapState, action: PayloadAction<NodeIdAndUniqueIdMapPayload>) => {
      const { fileVersionId, data } = action.payload;
      const cubeUniqueIdMap: Record<string, number[]> = {};
      if (data) {
        for (const i in data) {
          const mapKey = data[i] as string;
          if (!mapKey) continue;
          const existing = cubeUniqueIdMap[mapKey] ?? [];
          cubeUniqueIdMap[mapKey] = [...existing, Number(i)];
        }
      }
      return {
        ...state,
        [fileVersionId]: {
          nodeIdMap: data,
          cubeUniqueIdMap,
        },
      };
    },
  },
});

const bottomToolbarSlice = createSlice({
  name: "isBottomToolbarRefreshRequired",
  initialState: false,
  reducers: {
    setBottomToolbarRefresh: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const transparentElementsSlice = createSlice({
  name: "transparentElements",
  initialState: [] as number[],
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateTransparentElements: (_state: number[], action: PayloadAction<any[]>) => action.payload as number[],
  },
});

interface AnnotationItem {
  id: string;
  type?: string;
  markup?: string;
  pageNumber?: number;
  subject?: string;
  contents?: string;
  color?: string;
  opacity?: number;
  rect?: number[];
  vertices?: number[];
  fontSize?: number;
  fontFamily?: string;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  flags?: number;
  rotate?: number;
  createdAt?: number;
  updatedAt?: number;
  author?: string;
  userId?: string;
  issueId?: string;
  readOnly?: boolean;
  hidden?: boolean;
  customData?: Record<string, string | number | boolean | null>;
}

interface PageInfo {
  pageNumber: number;
  reason: string | null;
  zoomLevel: number;
  xScrollPosition: number;
  yScrollPosition: number;
}

interface ViewerStateShape {
  isSidebarOpen: boolean;
  markupAnnotations: AnnotationItem[];
  nonMarkupAnnotations: AnnotationItem[];
  currentPageInfo: PageInfo;
  showAllIssueMarkups: boolean;
  reason: string;
  showObjectTreeShimmer: boolean;
  // Allow additional dynamic viewer state fields (e.g. custom annotation types, feature flags)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const DEFAULT_VIEWER_STATE: ViewerStateShape = {
  isSidebarOpen: true,
  markupAnnotations: [],
  nonMarkupAnnotations: [],
  currentPageInfo: {
    pageNumber: 1,
    reason: null,
    zoomLevel: 1,
    xScrollPosition: 0,
    yScrollPosition: 0,
  },
  showAllIssueMarkups: false,
  reason: "reset_viewer_state",
  showObjectTreeShimmer: true,
};

interface SetAnnotationsPayload {
  annotationType?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: AnnotationItem[] | AnnotationItem | any;
  reason?: string;
}

interface DeleteAnnotationPayload {
  annotationType?: string;
  data: string;
  reason?: string;
}

interface UpdateViewerStatePayload {
  field: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: string | number | boolean | null | AnnotationItem[] | AnnotationItem | PageInfo | Record<string, any> | any[];
  reason?: string;
}

const viewerStateSlice = createSlice({
  name: "viewerState",
  initialState: DEFAULT_VIEWER_STATE,
  reducers: {
    resetViewerState: (state: ViewerStateShape) => ({ ...state, ...DEFAULT_VIEWER_STATE }),
    setViewerState: (state: ViewerStateShape, action: PayloadAction<Partial<ViewerStateShape>>) => {
      return {
        ...state,
        ...action.payload,
        reason: action.payload.reason ?? "",
      };
    },
    updateViewerState: (state: ViewerStateShape, action: PayloadAction<UpdateViewerStatePayload>) => ({
      ...state,
      [action.payload.field]: action.payload.value,
      reason: action.payload.reason ?? "",
    }),
    setAnnotations: (state: ViewerStateShape, action: PayloadAction<SetAnnotationsPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      return {
        ...state,
        [annotationType]: action.payload.data,
        reason: action.payload.reason ?? "",
      };
    },
    deleteAnnotation: (state: ViewerStateShape, action: PayloadAction<DeleteAnnotationPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const existing = (state[annotationType] as AnnotationItem[]) ?? [];
      return {
        ...state,
        [annotationType]: existing.filter((annotation: AnnotationItem) => annotation.id !== action.payload.data),
        reason: action.payload.reason ?? "",
      };
    },
    addAnnotation: (state: ViewerStateShape, action: PayloadAction<SetAnnotationsPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const isArray = Array.isArray(action.payload.data);
      const dataToAdd = isArray ? (action.payload.data as AnnotationItem[]) : [action.payload.data as AnnotationItem];
      const existing = (state[annotationType] as AnnotationItem[]) ?? [];
      return {
        ...state,
        [annotationType]: [...existing, ...dataToAdd],
        reason: action.payload.reason ?? "",
      };
    },
    updateAnnotationProperties: (
      state: ViewerStateShape,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: PayloadAction<{ annotationType?: string; data: { id: string; properties: Record<string, any> }; reason?: string }>,
    ) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const { id, properties } = action.payload.data;
      const existing = (state[annotationType] as AnnotationItem[]) ?? [];
      const targetIndex = existing.findIndex((annotation: AnnotationItem) => annotation.id === id);
      if (targetIndex === -1) return state;
      const targetAnnotation = existing[targetIndex]!;
      const hasChanges = Object.entries(properties).some(([key, value]) => {
        const isEqual = _.isEqual(targetAnnotation[key], value);
        return !isEqual;
      });
      if (!hasChanges) {
        return {
          ...state,
          reason: "unchanged_annotations",
        };
      }
      const newAnnotations = [...existing];
      newAnnotations[targetIndex] = { id, ...newAnnotations[targetIndex], ...properties } as AnnotationItem;
      return {
        ...state,
        [annotationType]: newAnnotations,
        reason: action.payload.reason ?? "",
      };
    },
    setCurrentPagePDF: (state: ViewerStateShape, action: PayloadAction<PageInfo>) => ({
      ...state,
      currentPageInfo: action.payload,
    }),
    setShowObjectTreeShimmer: (state: ViewerStateShape, action: PayloadAction<boolean>) => ({
      ...state,
      showObjectTreeShimmer: action.payload,
    }),
  },
});

interface SelectedMeasurementRecord {
  measurementId?: string;
  name?: string;
  type?: string;
  quantity?: number;
  scale?: string;
  markup?: string;
  takeOffItemId?: string;
  fileVersionId?: string;
  estimateId?: string;
  nestedMeasurements?: Array<{ measurementId: string; quantity: number }> | string | null;
  referenceMeasurements?: ReferenceMeasurement[] | null;
  formula?: string | null;
  manualUnit?: string | null;
  isManualMeasurement?: boolean;
  sheetId?: number;
}

const selectedNestedMeasurementsSlice = createSlice({
  name: "getSelectedNestedMeasurements",
  initialState: {} as SelectedMeasurementRecord,
  reducers: {
    setSelectedNestedMeasurements: (_state: SelectedMeasurementRecord, action: PayloadAction<SelectedMeasurementRecord>) =>
      action.payload,
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MeasurementCallback = ((...args: any[]) => void) | null | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MeasurementStructure = Record<string, any> | null | undefined;

interface MeasurementAttributePopupState {
  show: boolean;
  callback: MeasurementCallback;
  structure: MeasurementStructure;
}

const measurementAttributePopupSlice = createSlice({
  name: "measurementAttributePopup",
  initialState: { show: false, callback: null, structure: null } as MeasurementAttributePopupState,
  reducers: {
    setMeasurementAttributePopup: (
      _state: MeasurementAttributePopupState,
      action: PayloadAction<MeasurementAttributePopupState>,
    ) => ({ ...action.payload }),
  },
});

/** Subset of ViewRecord stored in reducer: the id and a deserialized viewpoint object. */
type DefaultViewState = Pick<ViewRecord, "_id"> & {
  viewpoint?: Record<string, string | number | boolean | null | Record<string, string | number | boolean | null>>;
};

const viewSlice = createSlice({
  name: "viewReducer",
  initialState: null as DefaultViewState | null,
  reducers: {
    setDefaultView: (_state: DefaultViewState | null, action: PayloadAction<DefaultViewState | null>) => action.payload,
  },
});

const scsFileLoadProgressSlice = createSlice({
  name: "scsFileLoadProgress",
  initialState: { isLoading: false, progress: 0 },
  reducers: {
    setScsFileLoadProgress: (state: { isLoading: boolean; progress: number }, action: PayloadAction<{ isLoading: boolean; progress: number }>) => ({
      ...state,
      ...action.payload,
    }),
    resetScsFileLoadProgress: () => ({ isLoading: false, progress: 0 }),
  },
});

export const { setModalStatus, setShowOnlyModal, setAnimationForIssueCreation } = hoopsModalSlice.actions;
export const { setIsSheetActive } = sheetActiveSlice.actions;
export const { setFaceSelection } = faceSelectionSlice.actions;
export const {
  setModelProperties,
  modifyMetadataUnits,
  updateCustomMetadataInModelProperties,
  updateAssetDataInModelProperties,
  updateQuantityAndUnit,
} = modelPropertiesSlice.actions;
export const { setSettingModalAPIData, updateSettingModalAPIData } = settingModalSlice.actions;
export const { setModifiedModelProps } = modifiedModelPropSlice.actions;
export const { setNodeIdAndUniqueIdMap } = nodeIdAndUniqueIdMapSlice.actions;
export const { setBottomToolbarRefresh } = bottomToolbarSlice.actions;
export const { updateTransparentElements } = transparentElementsSlice.actions;
export const {
  resetViewerState,
  setViewerState,
  updateViewerState,
  setAnnotations,
  deleteAnnotation,
  addAnnotation,
  updateAnnotationProperties,
  setCurrentPagePDF,
  setShowObjectTreeShimmer,
} = viewerStateSlice.actions;
export const { setSelectedNestedMeasurements } = selectedNestedMeasurementsSlice.actions;
export const { setMeasurementAttributePopup } = measurementAttributePopupSlice.actions;
export const { setDefaultView } = viewSlice.actions;
export const { setScsFileLoadProgress, resetScsFileLoadProgress } = scsFileLoadProgressSlice.actions;

export const HoopsModalStatus = hoopsModalSlice.reducer;
export const isSheetActive = sheetActiveSlice.reducer;
export const faceSelection = faceSelectionSlice.reducer;
export const modelProperties = modelPropertiesSlice.reducer;
export const GetSettingModalData = settingModalSlice.reducer;
export const nodeIdAndUniqueIdMap = nodeIdAndUniqueIdMapSlice.reducer;
export const isBottomToolbarRefreshRequired = bottomToolbarSlice.reducer;
export const transparentElements = transparentElementsSlice.reducer;
export const viewerState = viewerStateSlice.reducer;
export const getSelectedNestedMeasurements = selectedNestedMeasurementsSlice.reducer;
export const measurementAttributePopup = measurementAttributePopupSlice.reducer;
export const viewReducer = viewSlice.reducer;
export const modifiedModelProps = modifiedModelPropSlice.reducer;
export const scsFileLoadProgress = scsFileLoadProgressSlice.reducer;
